import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import camelCase from 'lodash/camelCase';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import orderBy from 'lodash/orderBy';
import { ShapeSelect, AlertNotice } from 'shared/components';
import './RoomStructure.scss';

const transformOptions = (arr) =>
  arr.map((item) => {
    const i = { ...item };
    i.key = camelCase(item.name);
    return i;
  });

class RoomStructure extends Component {
  constructor(props) {
    super(props);
    this.state = {
      shape: props.room.shape || {},
      seating: props.room.seating || {},
      deskType: props.room.deskType || {},
      supervisorDesk: props.room.supervisorDesk || {},
      options: {
        shape: transformOptions(props.roomOptions.shapes),
        seating: transformOptions(props.roomOptions.seating),
        deskTypes: transformOptions(props.roomOptions.deskTypes),
        supervisorDesk: transformOptions(props.roomOptions.supervisorDesk),
      },
    };

    this.onSubmit = this.onSubmit.bind(this);
    this.onSelect = this.onSelect.bind(this);
    this.isDisabled = this.isDisabled.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.room !== this.props.room) {
      this.setState({
        shape: this.props.room.shape || {},
        seating: this.props.room.seating || {},
        deskType: this.props.room.deskType || {},
        supervisorDesk: this.props.room.supervisorDesk || {},
      });
    }
  }

  onSubmit(e) {
    e.preventDefault();

    this.props.onContinue({
      shape: omit(this.state.shape, ['key']),
      seating: omit(this.state.seating, ['key']),
      deskType: omit(this.state.deskType, ['key']),
      supervisorDesk: omit(this.state.supervisorDesk, ['key']),
    });
  }

  onSelect(type, selected) {
    this.setState({
      [type]: selected,
    });
  }

  isDisabled() {
    const { state } = this;
    return (
      isEmpty(state.shape) ||
      isEmpty(state.seating) ||
      isEmpty(state.deskType) ||
      isEmpty(state.supervisorDesk)
    );
  }

  render() {
    const { shape, seating, deskTypes, supervisorDesk } = this.state.options;
    const { errorRoom } = this.props;

    return (
      <form className="room-structure" onSubmit={this.onSubmit} noValidate>
        <h4>{this.props.t('roomBuilder.roomStructureTitle')}</h4>
        <p>{this.props.t('roomBuilder.roomStructureCopy')}</p>
        <h5>{this.props.t('roomBuilder.roomShapeLabel')}</h5>
        <div className="shapes-container">
          {orderBy(shape, 'displayOrder').map((s) => (
            <ShapeSelect
              key={`option-${s.key}`}
              selected={this.state.shape.id === s.id}
              shape={s.key}
              label={this.props.t(`roomBuilder.shapes.${s.key}`)}
              onClick={() => {
                this.onSelect('shape', s);
              }}
            />
          ))}
        </div>
        <h5>{this.props.t('roomBuilder.seatingArrangementLabel')}</h5>
        <div className="shapes-container">
          {orderBy(seating, 'displayOrder').map((s) => (
            <ShapeSelect
              key={`option-${s.key}`}
              selected={this.state.seating.id === s.id}
              shape={s.key}
              label={this.props.t(`roomBuilder.shapes.${s.key}`)}
              onClick={() => {
                this.onSelect('seating', s);
              }}
            />
          ))}
        </div>
        <h5>{this.props.t('roomBuilder.deskTypesLabel')}</h5>
        <div className="shapes-container">
          {orderBy(deskTypes, 'displayOrder').map((s) => (
            <ShapeSelect
              key={`option-${s.key}`}
              selected={this.state.deskType.id === s.id}
              shape={s.key}
              label={this.props.t(`roomBuilder.shapes.${s.key}`)}
              onClick={() => {
                this.onSelect('deskType', s);
              }}
            />
          ))}
        </div>
        <h5>{this.props.t('roomBuilder.supervisorDeskLocationsLabel')}</h5>
        <div className="shapes-container">
          {orderBy(supervisorDesk, 'displayOrder').map((s) => (
            <ShapeSelect
              key={`option-${s.key}`}
              selected={this.state.supervisorDesk.id === s.id}
              shape={s.key}
              label={this.props.t(`roomBuilder.shapes.${s.key}`)}
              onClick={() => {
                this.onSelect('supervisorDesk', s);
              }}
            />
          ))}
        </div>
        {!isEmpty(errorRoom) && (
          <div className="mt-3">
            <AlertNotice message={errorRoom.message} type="error" />
          </div>
        )}
        <div className="buttons-container w-100">
          <button type="button" className="btn btn-outline w-50" onClick={this.props.onBack}>
            {this.props.t('common.labels.back')}
          </button>
          <button type="submit" className="btn btn-primary w-50" disabled={this.isDisabled()}>
            {this.props.t('common.labels.continue')}
          </button>
        </div>
      </form>
    );
  }
}

RoomStructure.propTypes = {
  room: PropTypes.shape({
    shape: PropTypes.shape({}),
    seating: PropTypes.shape({}),
    deskType: PropTypes.shape({}),
    supervisorDesk: PropTypes.shape({}),
  }).isRequired,
  roomOptions: PropTypes.shape({
    shapes: PropTypes.arrayOf(PropTypes.shape({})),
    seating: PropTypes.arrayOf(PropTypes.shape({})),
    deskTypes: PropTypes.arrayOf(PropTypes.shape({})),
    supervisorDesk: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  onBack: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

export default withTranslation()(RoomStructure);
